import React, { useId } from "react";
import { rankInfo } from "../../lib/ranks";

/**
 * A rank's emblem, drawn in code at any size (the pills' 14 px to the popup's 96 px): a shield
 * rimmed in the rank's metal over a dark enamel field, the logo's spade in the middle, the
 * division as pips under it (III one, I three). Or and above get a laurel, Diamant a gem instead
 * of the spade, Maître a crown. A player still calibrating shows a grey shield with « ? ».
 * Original drawing (no game's emblem).
 */
const METAL = {
  bronze: ["#f6c99a", "#c47a42", "#5e2f12", "#2a1508", "#4a2612"],
  argent: ["#ffffff", "#b8c2ce", "#4f5b69", "#1e2733", "#334155"],
  or: ["#fff3b8", "#e8b53a", "#6b4708", "#2b1f05", "#4d3608"],
  platine: ["#eafffb", "#7dd3c7", "#276560", "#0b2a2a", "#134e4a"],
  diamant: ["#e3f3ff", "#5aaeff", "#1d3f99", "#0a1a3d", "#1e3a8a"],
  maitre: ["#ffd0dd", "#e3456c", "#5b0b25", "#2a0613", "#5b0b25"],
  calibrating: ["#e2e8f0", "#94a3b8", "#475569", "#0f172a", "#1e293b"],
};
const GOLD = ["#fff3b8", "#e8b53a", "#6b4708"];

const RIM = "M40 8L68 17V41C68 59 56 72 40 80C24 72 12 59 12 41V17Z";
const FIELD = "M40 14L62 21V41C62 55 53 66 40 73C27 66 18 55 18 41V21Z";
const SHINE = "M40 10L66 18.5V27C56 22 47 20 40 20C33 20 24 22 14 27V18.5Z";
const SPADE = "M50 4C62 22 96 40 96 62C96 76 85 84 74 84C64 84 57 79 53 72C54 82 58 90 66 96H34C42 90 46 82 47 72C43 79 36 84 26 84C15 84 4 76 4 62C4 40 38 22 50 4Z";
const LEAF = "M0 0C3-3.6 9-3.6 13 0C9 3.6 3 3.6 0 0Z";
const LEAVES = [[15, 74, -55], [10, 64, -72], [7, 53, -86], [7, 42, -98], [9, 31, -112]];
const CROWN = "M25 15L27 3L33.5 9.5L40 1L46.5 9.5L53 3L55 15Z";
const PIP = "M0-3.2L3.2 0L0 3.2L-3.2 0Z";

export function RankEmblem({ rank, info, size = 32, className = "", title }) {
  const uid = useId().replace(/:/g, "");
  const r = info || rankInfo(rank);
  const kind = r.calibrating ? "calibrating" : r.tier;
  const [light, mid, dark, fieldIn, fieldOut] = METAL[kind];
  const laurel = !r.calibrating && r.tierIndex >= 2;
  const pips = !r.calibrating && r.division ? 4 - r.division : 0;
  const detailed = size >= 28;
  const m = `${uid}m`;
  const f = `${uid}f`;
  const g = `${uid}g`;

  return (
    <svg
      viewBox="0 0 80 88"
      width={(size * 80) / 88}
      height={size}
      className={`shrink-0 ${className}`}
      role={title ? "img" : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
    >
      {title && <title>{title}</title>}
      <defs>
        <linearGradient id={m} x1="0" y1="0" x2="0.35" y2="1">
          <stop offset="0" stopColor={light} />
          <stop offset="0.45" stopColor={mid} />
          <stop offset="1" stopColor={dark} />
        </linearGradient>
        <radialGradient id={f} cx="0.5" cy="0.38" r="0.7">
          <stop offset="0" stopColor={fieldOut} />
          <stop offset="1" stopColor={fieldIn} />
        </radialGradient>
        <linearGradient id={g} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={GOLD[0]} />
          <stop offset="0.5" stopColor={GOLD[1]} />
          <stop offset="1" stopColor={GOLD[2]} />
        </linearGradient>
      </defs>

      {laurel && detailed && (
        <g fill={`url(#${g})`} opacity="0.95">
          {LEAVES.map(([x, y, a], i) => (
            <path key={`l${i}`} d={LEAF} transform={`translate(${x} ${y}) rotate(${a})`} />
          ))}
          <g transform="translate(80 0) scale(-1 1)">
            {LEAVES.map(([x, y, a], i) => (
              <path key={`r${i}`} d={LEAF} transform={`translate(${x} ${y}) rotate(${a})`} />
            ))}
          </g>
        </g>
      )}

      <path d={RIM} fill={`url(#${m})`} />
      <path d={FIELD} fill={`url(#${f})`} />
      {detailed && <path d={SHINE} fill="#ffffff" opacity="0.18" />}

      {r.calibrating ? (
        <text x="40" y="53" textAnchor="middle" fontSize="30" fontFamily="Outfit, sans-serif" fill={`url(#${m})`}>?</text>
      ) : r.tier === "diamant" ? (
        <g>
          <path d="M40 28L54 38L40 60L26 38Z" fill={`url(#${m})`} />
          <path d="M26 38H54M40 28L34 38L40 60L46 38Z" fill="none" stroke={dark} strokeWidth="1.2" strokeLinejoin="round" opacity="0.7" />
        </g>
      ) : (
        <path d={SPADE} fill={`url(#${m})`} transform="translate(28.5 28) scale(0.23)" />
      )}

      {detailed && pips > 0 && (
        <g fill={`url(#${m})`}>
          {Array.from({ length: pips }, (_, i) => (
            <path key={i} d={PIP} transform={`translate(${40 + (i - (pips - 1) / 2) * 8.5} 66)`} />
          ))}
        </g>
      )}

      {r.tier === "maitre" && !r.calibrating && <path d={CROWN} fill={`url(#${g})`} stroke={GOLD[2]} strokeWidth="0.8" />}
    </svg>
  );
}

export default RankEmblem;
